import { CheckIcon } from "@chakra-ui/icons";
import {
  Box,
  Flex,
  HStack,
  Icon,
  Spinner,
  Tag,
  Text,
  useDisclosure,
  VStack,
} from "@chakra-ui/react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { RiFlaskLine } from "react-icons/ri";
import { ResizableDrawer } from "src/components/Layout/ResizableDrawer";
import PolishSvg from "src/images/polish.svg?react";

type PolishStepStatus = "completed" | "in-progress" | "pending";

type PolishStep = {
  title: string;
  description: string;
  status: PolishStepStatus;
};

type PolishProgressDrawerProps = {
  isOpen: boolean;
  onClose: () => void;
  currentStep: number;
};

function StepIcon({ status }: { status: PolishStepStatus }) {
  switch (status) {
    case "completed":
      return (
        <Box as={CheckIcon} boxSize={5} color="green.500" flexShrink={0} />
      );
    case "in-progress":
      return (
        <Spinner
          size="sm"
          color="blue.500"
          thickness="3px"
          speed="0.8s"
          flexShrink={0}
        />
      );
    case "pending":
      return (
        <Box
          width={5}
          height={5}
          borderRadius="full"
          border="2px solid"
          borderColor="gray.300"
          flexShrink={0}
        />
      );
  }
}

function StepCard({ step }: { step: PolishStep }) {
  return (
    <Box
      p={4}
      borderRadius="md"
      border="1px solid"
      borderColor="gray.200"
      bg="white"
      width="100%"
    >
      <HStack spacing={3} align="flex-start">
        <StepIcon status={step.status} />
        <VStack align="flex-start" spacing={1} flex={1}>
          <Text fontWeight="medium" fontSize="sm">
            {step.title}
          </Text>
          <Text fontSize="xs" color="gray.600">
            {step.description}
          </Text>
        </VStack>
      </HStack>
    </Box>
  );
}

export function PolishProgressDrawer({
  isOpen,
  onClose,
  currentStep,
}: PolishProgressDrawerProps) {
  const steps: PolishStep[] = useMemo(
    () => [
      {
        title: "Analyzing the foundation",
        description:
          "Reading and understanding the original text, intent and context",
        status:
          currentStep === 0
            ? "in-progress"
            : currentStep >= 1
            ? "completed"
            : "pending",
      },
      {
        title: "Buffing to a brilliant shine",
        description: "Shaping every sentence to be crystal clear",
        status:
          currentStep === 1
            ? "in-progress"
            : currentStep >= 2
            ? "completed"
            : "pending",
      },
      {
        title: "Adding the final gleam",
        description: "One last check for that perfect finish",
        status:
          currentStep >= 3
            ? "completed"
            : currentStep === 2
            ? "in-progress"
            : "pending",
      },
    ],
    [currentStep]
  );

  return (
    <ResizableDrawer
      isOpen={isOpen}
      onClose={onClose}
      header={
        <Flex direction="column" gap={1}>
          <Text fontSize="lg" fontWeight="400">
            Draft with Avela Intelligence
          </Text>
          <Box>
            <Tag size="sm" variant="outline" colorScheme="purple" gap={1}>
              <Icon as={RiFlaskLine} />
              <Text>Beta</Text>
            </Tag>
          </Box>
        </Flex>
      }
      contentProps={{
        bg: "white",
        minWidthPercent: 30,
        initialWidthPercent: 30,
      }}
    >
      <VStack spacing={6} align="stretch" py={4}>
        <Flex
          justify="center"
          align="center"
          width="100%"
          height="120px"
          position="relative"
          mb={4}
        >
          <PolishSvg />
        </Flex>

        <VStack spacing={2} align="center">
          <Text fontSize="xl" fontWeight="semibold">
            Polishing message
          </Text>
          <Text fontSize="sm" color="gray.600" textAlign="center">
            Avela Intelligence may take a few seconds to put it all together
          </Text>
        </VStack>

        <VStack spacing={3} align="stretch" width="100%">
          {steps.map((step, index) => (
            <StepCard key={index} step={step} />
          ))}
        </VStack>
      </VStack>
    </ResizableDrawer>
  );
}

export type UsePolishProgressReturn = {
  isOpen: boolean;
  onOpen: () => void;
  onClose: () => void;
  cancel: () => void;
  currentStep: number;
  onPreparations: () => void;
  onPolishing: () => void;
  onFinishing: () => void;
  onCompleted: () => void;
};
export const usePolishProgress = (): UsePolishProgressReturn => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [currentStep, setCurrentStep] = useState(0);
  const timeoutRefs = useRef<NodeJS.Timeout[]>([]);

  useEffect(() => {
    return () => {
      timeoutRefs.current.forEach((timeout) => clearTimeout(timeout));
      timeoutRefs.current = [];
    };
  }, []);

  const delayedOnClose = useCallback(() => {
    const timeout = setTimeout(() => {
      onClose();
    }, 500);
    timeoutRefs.current.push(timeout);
  }, [onClose]);

  const cancel = useCallback(() => {
    timeoutRefs.current.forEach((timeout) => clearTimeout(timeout));
    timeoutRefs.current = [];
    onClose();
    setCurrentStep(0);
  }, [onClose]);

  const onPreparations = useCallback(() => setCurrentStep(0), []);

  const onPolishing = useCallback(() => {
    const timeout = setTimeout(() => setCurrentStep(1), 500);
    timeoutRefs.current.push(timeout);
  }, []);

  const onFinishing = useCallback(() => setCurrentStep(2), []);

  const onCompleted = useCallback(() => {
    const timeout = setTimeout(() => setCurrentStep(3), 300);
    timeoutRefs.current.push(timeout);
  }, []);

  return {
    isOpen,
    onOpen,
    onClose: delayedOnClose,
    cancel,
    currentStep,
    onPreparations,
    onPolishing,
    onFinishing,
    onCompleted,
  };
};
